import { Error as ErrorIcon } from '@mui/icons-material';
import { Chip, Grid } from '@mui/material';
import { calculateDuration, dateFormatShortTime, parseSecondsToHM } from '@nx-smartmonkey/shared/helpers';
import { HSpacer, RoutalPalette, RoutalTooltip } from '@nx-smartmonkey/ui';
import { get } from 'lodash';
import { useState } from 'react';
import styled from 'styled-components';

import More3Points from '../../../../assets/svg/More3Points';
import CommentsIcon from '../../../../assets/svg/Plan/report/CommentsIcon';
import CustomFieldsIcon from '../../../../assets/svg/Plan/report/CustomFieldsIcon';
import NobodyAtHomeIcon from '../../../../assets/svg/Plan/report/NobodyAtHomeIcon';
import OtherCancelReasons from '../../../../assets/svg/Plan/report/OtherCancelReasons';
import PhotosIcon from '../../../../assets/svg/Plan/report/PhotosIcon';
import SignatureIcon from '../../../../assets/svg/Plan/report/SignatureIcon';
import WrongAddressIcon from '../../../../assets/svg/Plan/report/WrongAddressIcon';
import { numericToText } from '../../../../components/form-fields/FormTimeWindows';
import { StopsActionsMenu } from '../../../../components/menus/StopsActionsMenu';
import { STOP_STATUS } from '../../../../constants/Stops';
import { PLAN_TYPE } from '../../../../constants/Subscriptions';
import { Stop } from '../../../../domain/stop/Stop';
import { intl } from '../../../../helpers/IntGlobalProvider';
import { useRetrieveOrganization } from '../../../../hooks/organization/useRetrieveOrganization';
import { useRetrieveActivePlan } from '../../../../hooks/plan/useRetrieveActivePlan';
import { useRetrieveActivePlanDrivers } from '../../../../hooks/plan/useRetrieveActivePlanDrivers';
import { useRetrieveActivePlanStops } from '../../../../hooks/plan/useRetrieveActivePlanStops';
import { useSetActivePlanSelectedStops } from '../../../../hooks/plan/useSetActivePlanSelectedStops';
import { useRetrieveActiveProject } from '../../../../hooks/project/useRetrieveActiveProject';
import { useRetrieveRightPanelInfo } from '../../../../hooks/right-panel/useRetrieveRightPanelInfo';
import Text from '../../../../react-components/Text';
import StopCircleColor from '../../../shared/StopCircleColor';
import { stopTimewindowsAreInvalid } from '../../shared/BrokenConstraintsTooltip';
import ProblemsTooltip from '../../shared/ProblemsTooltip';
import { StopStatusBar } from '../StatusBar';

const StopChipContainer = styled(Grid)`
  margin-bottom: 4px;
  border: 1px solid ${RoutalPalette.neutral10};
  border-radius: 4px;
  background-color: white;
`;

const StopChipHeader = styled(Grid)`
  padding: 4px;
  height: 58px;
`;
const StopHeaderRow = styled(Grid)`
  height: 24px;
  padding-left: 8px;
  padding-right: 8px;
`;

const StopChipCounterBadge = styled(Grid)`
  position: absolute;
  right: -8px;
  top: -8px;
  background-color: ${RoutalPalette.primary40};
  color: ${RoutalPalette.neutral00};
  border-radius: 50%;
  height: 22px;
  width: 22px;
  text-align: center;
  font-size: 12px;
`;

export const StopChip = ({ stop, isDragging }: { stop: Stop; isDragging: boolean }) => {
  const { map: driversMap, data: drivers } = useRetrieveActivePlanDrivers();
  const { map: stopsMap, data: stops } = useRetrieveActivePlanStops();
  const { data: plan } = useRetrieveActivePlan();
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: organization } = useRetrieveOrganization();
  const { selectedStops, setSelectedStops } = useSetActivePlanSelectedStops();
  const { setRightPanelInfo } = useRetrieveRightPanelInfo();

  const isSelected = selectedStops.includes(stop.id);
  const driver = get(driversMap, stop.driver_id || ``, undefined);

  const [isHover, setIsHover] = useState(false);

  const [contextAnchorStopChip, setContextAnchorStopChip] = useState<
    | {
        screenPosition: {
          top: number;
          left: number;
        };
      }
    | undefined
  >(undefined);

  const onDoubleClickStop = () => {
    setRightPanelInfo({
      detailType: `stop`,
      detailId: `${stop.id}`,
      detailTab: null,
    });
  };

  const renderCompleted = () => {
    const planReport = (stop.reports ?? []).find((report) => report.plan_id === plan?.id);

    return (
      <Grid container direction="row">
        {planReport?.cancel_reason && (
          <RoutalTooltip
            mouseEnterDelay={0.5}
            mouseLeaveDelay={0}
            title={intl.formatMessage({ id: `properties.stop.report.reason.${planReport.cancel_reason}` })}
            placement="right"
            backgroundColor={RoutalPalette.statusColors.canceled}
            arrowColor={RoutalPalette.statusColors.canceled}
          >
            <Grid
              style={{
                height: `18px`,
                padding: `4px 8px`,
                display: `flex`,
                alignItems: `center`,
                position: `relative`,
                borderRadius: `5px`,
                marginRight: `4px`,
                backgroundColor:
                  stop.status === STOP_STATUS.completed
                    ? RoutalPalette.statusColors.completed
                    : stop.status === STOP_STATUS.incomplete
                    ? RoutalPalette.statusColors.incomplete
                    : RoutalPalette.statusColors.canceled,
              }}
            >
              {(() => {
                if (planReport.cancel_reason === `nobody`) {
                  return <NobodyAtHomeIcon style={{ height: `16px`, width: `16px` }} />;
                }
                if (planReport.cancel_reason === `wrong_address`) {
                  return <WrongAddressIcon style={{ height: `16px`, width: `16px` }} />;
                }
                return <OtherCancelReasons style={{ height: `16px`, width: `16px` }} />;
              })()}
            </Grid>
          </RoutalTooltip>
        )}

        {planReport?.comments && (
          <Grid
            style={{
              height: `18px`,
              padding: `4px 8px`,
              display: `flex`,
              alignItems: `center`,
              position: `relative`,
              borderRadius: `5px`,
              marginRight: `4px`,
              backgroundColor:
                stop.status === STOP_STATUS.completed
                  ? RoutalPalette.statusColors.completed
                  : stop.status === STOP_STATUS.incomplete
                  ? RoutalPalette.statusColors.incomplete
                  : RoutalPalette.statusColors.canceled,
            }}
          >
            <CommentsIcon
              secondaryColor={
                stop.status === STOP_STATUS.completed
                  ? RoutalPalette.statusColors.completed
                  : stop.status === STOP_STATUS.incomplete
                  ? RoutalPalette.statusColors.incomplete
                  : RoutalPalette.statusColors.canceled
              }
            />
          </Grid>
        )}

        {planReport?.signature && (
          <Grid
            style={{
              height: `18px`,
              padding: `4px 8px`,
              display: `flex`,
              alignItems: `center`,
              position: `relative`,
              borderRadius: `5px`,
              marginRight: `4px`,
              backgroundColor:
                stop.status === STOP_STATUS.completed
                  ? RoutalPalette.statusColors.completed
                  : stop.status === STOP_STATUS.incomplete
                  ? RoutalPalette.statusColors.incomplete
                  : RoutalPalette.statusColors.canceled,
            }}
          >
            <SignatureIcon />
          </Grid>
        )}

        {(planReport?.images ?? []).length > 0 && (
          <Grid
            style={{
              height: `18px`,
              padding: `4px 8px`,
              display: `flex`,
              alignItems: `center`,
              position: `relative`,
              borderRadius: `5px`,
              marginRight: `4px`,
              backgroundColor:
                stop.status === STOP_STATUS.completed
                  ? RoutalPalette.statusColors.completed
                  : stop.status === STOP_STATUS.incomplete
                  ? RoutalPalette.statusColors.incomplete
                  : RoutalPalette.statusColors.canceled,
            }}
          >
            <PhotosIcon />
          </Grid>
        )}

        {planReport?.custom_fields?.props && Object.keys(planReport.custom_fields.props).length !== 0 && (
          <Grid
            style={{
              height: `18px`,
              padding: `4px 8px`,
              display: `flex`,
              alignItems: `center`,
              position: `relative`,
              borderRadius: `5px`,
              marginRight: `4px`,
              backgroundColor:
                stop.status === STOP_STATUS.completed
                  ? RoutalPalette.statusColors.completed
                  : stop.status === STOP_STATUS.incomplete
                  ? RoutalPalette.statusColors.incomplete
                  : RoutalPalette.statusColors.canceled,
            }}
          >
            <CustomFieldsIcon />
          </Grid>
        )}
      </Grid>
    );
  };

  const renderPending = () => (
    <>
      <Grid
        item
        style={{
          position: `absolute`,
          left: `14px`,
          top: `4px`,
          width: `24px`,
          height: `24px`,
          textAlign: `left`,
        }}
      >
        <ProblemsTooltip type="stop" stop={stop} tooltipPlacement="right" planDrivers={drivers} driver={driver}>
          <ErrorIcon
            style={{
              color: RoutalPalette.danger,
              fontSize: `16px`,
              backgroundColor: RoutalPalette.neutral00,
              borderRadius: `50%`,
              marginTop: `-4px`,
              marginLeft: `-4px`,
            }}
          />
        </ProblemsTooltip>
      </Grid>
      {/* TODO: WEIGHT ERRORS */}
      {stop.pickup && (
        <Chip
          style={{
            height: `18px`,
            marginRight: `2px`,
          }}
          label={<Text variant="label" style={{ color: RoutalPalette.secondary00 }} intlId="with_pickup" />}
        />
      )}
      {stop.time_windows && stop.time_windows.length > 0 && activeProject?.view?.constraints?.time_windows && (
        <Chip
          style={{
            height: `18px`,
            marginRight: `2px`,
            backgroundColor: stopTimewindowsAreInvalid(stop) ? RoutalPalette.danger : RoutalPalette.secondary20n,
          }}
          label={
            <Text
              variant="label"
              style={{
                color: stopTimewindowsAreInvalid(stop) ? RoutalPalette.neutral00 : RoutalPalette.neutral40,
              }}
            >{`${numericToText(stop.time_windows[0])} ${stop.time_windows.length > 1 ? `+` : ``}`}</Text>
          }
        />
      )}
      {stop.duration !== undefined && (
        <Chip
          style={{
            height: `18px`,
            marginRight: `2px`,
            backgroundColor: RoutalPalette.secondary20n,
          }}
          label={
            <Text variant="label" style={{ color: RoutalPalette.neutral40 }}>{`${calculateDuration(
              stop.duration
            )} min`}</Text>
          }
        />
      )}
      {/* TODO: WEIGHT ERRORS */}
      {stop.weight && activeProject?.view?.constraints?.weight ? (
        <Chip
          style={{
            height: `18px`,
            marginRight: `2px`,
            backgroundColor: RoutalPalette.secondary20n,
          }}
          label={
            <Text variant="label" style={{ color: RoutalPalette.neutral40 }}>{`${
              Math.floor(stop.weight) !== stop.weight ? stop.weight.toFixed(2) : stop.weight
            } ${activeProject?.units?.weight}`}</Text>
          }
        />
      ) : null}
      {/* TODO: VOLUME ERRORS */}
      {stop.volume && activeProject?.view?.constraints?.volume ? (
        <Chip
          style={{
            height: `18px`,
            backgroundColor: RoutalPalette.secondary20n,
          }}
          label={
            <Text variant="label" style={{ color: RoutalPalette.neutral40 }}>{`${
              Math.floor(stop.volume) !== stop.volume ? stop.volume.toFixed(2) : stop.volume
            } ${activeProject?.units?.volume}`}</Text>
          }
        />
      ) : null}
    </>
  );

  const getTimeDiff = (date1: Date, date2: number): React.ReactElement => {
    const hours = new Date(date1).getHours();
    const minutes = new Date(date1).getMinutes();

    const seconds = hours * 3600 + minutes * 60;
    const difference = seconds - date2;

    const diffHours = Math.abs(Math.floor(difference / 3600));
    const diffMinutes = Math.abs(Math.floor((difference % 3600) / 60));

    const symbol = difference > 0 ? `+` : `-`;
    const str = diffHours > 0 ? `${diffHours}h` : `${diffMinutes}m`;

    return (
      <Text variant="label" style={{ color: `#aaa` }}>
        {symbol}
        {str}
      </Text>
    );
  };

  const renderTime = () => {
    const planReport = (stop.reports ?? []).find((report) => report.plan_id === plan?.id);

    if (planReport && stop.status !== `pending`) {
      return (
        <>
          <Text
            variant="body"
            style={{
              color:
                planReport.type === `service_report_completed`
                  ? RoutalPalette.statusColors.completed
                  : RoutalPalette.statusColors.canceled,
            }}
          >
            {String(new Date(planReport.created_at).getHours()).padStart(2, `0`)}:
            {String(new Date(planReport.created_at).getMinutes()).padStart(2, `0`)}
          </Text>

          {getTimeDiff(planReport.created_at, stop?.planned_arrival_time || 0)}
        </>
      );
    }

    if (stop.estimated_arrival_time) {
      return (
        <>
          <Text
            variant="body"
            style={{
              color: RoutalPalette.warning,
              fontWeight: 600,
            }}
          >
            {dateFormatShortTime(stop.estimated_arrival_time)}
          </Text>

          {getTimeDiff(stop.estimated_arrival_time, stop?.planned_arrival_time || 0)}
        </>
      );
    }

    if (stop.planned_arrival_time || stop.planned_arrival_time === 0) {
      return (
        <Text
          variant="body"
          style={{
            color: RoutalPalette.secondary00,
            fontWeight: 600,
          }}
        >
          {parseSecondsToHM(stop.planned_arrival_time)}
        </Text>
      );
    }

    return null;
  };

  return (
    <StopChipContainer
      container
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      alignContent="space-between"
      onDoubleClick={(e) => {
        e.stopPropagation();
        onDoubleClickStop();
      }}
      style={{ backgroundColor: isSelected ? RoutalPalette.secondary20n : `white`, position: `relative` }}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isSelected) {
          setSelectedStops([stop.id]);
        }
        setContextAnchorStopChip({
          screenPosition: {
            top: e.clientY,
            left: e.clientX,
          },
        });
      }}
      onClick={(e) => {
        if (
          e.shiftKey &&
          selectedStops.length >= 1 &&
          selectedStops[selectedStops.length - 1] in stopsMap &&
          stopsMap[selectedStops[selectedStops.length - 1]].driver_id === stop.driver_id
        ) {
          let newSelection: string[];
          const selectedStop = stopsMap[selectedStops[selectedStops.length - 1]];
          if (stop.driver_id) {
            const driverStops = driver?.stops || [];
            const start = Math.min(stop.order!, selectedStop.order!);
            const end = Math.max(stop.order!, selectedStop.order!);
            // get a sublists of stops between start and end
            newSelection = [
              ...selectedStops,
              ...driverStops.filter((stop) => stop.order! >= start && stop.order! <= end).map((stop) => stop.id),
            ];
          } else {
            const unassignedStops = stops!.filter((stop) => !stop.driver_id);
            const stopPosition = unassignedStops.findIndex((s) => s.id === stop.id);
            const selectedStopPosition = unassignedStops.findIndex((s) => s.id === selectedStop.id);
            const start = Math.min(stopPosition, selectedStopPosition);
            const end = Math.max(stopPosition, selectedStopPosition);
            // get a sublists of stops between start and end
            newSelection = [
              ...selectedStops,
              ...unassignedStops.filter((stop, index) => index >= start && index <= end).map((stop) => stop.id),
            ];
          }
          // remove possible duplicates
          const newSelectionSet = new Set(newSelection);
          const newSelectionUnique = Array.from(newSelectionSet);
          setSelectedStops(newSelectionUnique);
        } else if (e.ctrlKey || e.metaKey) {
          if (selectedStops.includes(stop.id)) {
            setSelectedStops(selectedStops.filter((id) => id !== stop.id));
          } else {
            setSelectedStops([...selectedStops, stop.id]);
          }
        } else {
          setSelectedStops([stop.id]);
        }
      }}
    >
      {/* Dragging badge */}
      {isDragging && selectedStops.length > 1 && (
        <StopChipCounterBadge item>
          <Grid container justifyContent="center" alignContent="center" style={{ height: `100%` }}>
            {selectedStops.length}
          </Grid>
        </StopChipCounterBadge>
      )}

      <StopChipHeader container direction="row" wrap="nowrap">
        <StopStatusBar status={stop.status} />
        <Grid container alignContent="center">
          <StopHeaderRow container direction="row" alignItems="center">
            <StopCircleColor
              driver={driver}
              status={stop.status}
              isPickup={stop.type === `pickup`}
              number={stop.order != null ? stop.order + 1 : undefined}
              planDrivers={drivers ?? []}
              isSelected={false}
            />
            <HSpacer small />
            <RoutalTooltip
              mouseEnterDelay={0.5}
              mouseLeaveDelay={0}
              id={stop.id}
              title={stop.label ?? ``}
              placement="top"
            >
              {(stop.label ?? ``) !== `` ? (
                <Text
                  variant="body"
                  style={{
                    fontWeight: 600,
                    cursor: `pointer`,
                    width: `250px`,
                    textOverflow: `ellipsis`,
                    overflow: `hidden`,
                    whiteSpace: `nowrap`,
                  }}
                >
                  {stop.label}
                </Text>
              ) : (
                <Text
                  variant="body"
                  intlId="no_name"
                  style={{ color: RoutalPalette.secondary00, fontWeight: 500, cursor: `pointer` }}
                />
              )}
            </RoutalTooltip>
          </StopHeaderRow>
          <StopHeaderRow
            container
            direction="row"
            alignItems="center"
            wrap="nowrap"
            style={{ overflow: `hidden`, marginTop: `1px` }}
          >
            {stop.status !== `pending` && renderCompleted()}
            {stop.status === `pending` && renderPending()}
          </StopHeaderRow>
        </Grid>
      </StopChipHeader>

      <Grid
        item
        style={{
          position: `absolute`,
          right: `4px`,
          top: `12px`,
          width: `62px`,
          textAlign: `right`,
          paddingRight: `10px`,
        }}
      >
        {renderTime()}
      </Grid>

      {isHover && (
        <Grid
          container
          style={{
            position: `absolute`,
            width: `50px`,
            height: `100%`,
            right: 0,
            background: isSelected
              ? `linear-gradient(90deg, ${RoutalPalette.secondary20n + `00`} 0%, ${RoutalPalette.secondary20n} 30%, ${
                  RoutalPalette.secondary20n
                } 100%)`
              : `linear-gradient(90deg, ${RoutalPalette.neutral00 + `00`} 0%, ${RoutalPalette.neutral00} 30%, ${
                  RoutalPalette.neutral00
                } 100%)`,
            borderRadius: `0 2px 2px 0`,
          }}
          justifyContent="center"
          alignContent="center"
        >
          <StopsActionsMenu
            type="popover"
            stops={[stop]}
            showOpenCustomers={organization?.subscription.plan !== PLAN_TYPE.MONKEY}
            popoverProps={{
              hideArrow: true,
              content: <More3Points />,
              style: { color: RoutalPalette.secondary60, padding: 0, margin: 0, border: `unset` },
            }}
          />
        </Grid>
      )}
      {contextAnchorStopChip && (
        <StopsActionsMenu
          type="contextual"
          setContextAnchor={setContextAnchorStopChip}
          showOpenCustomers={organization?.subscription.plan !== PLAN_TYPE.MONKEY}
          contextAnchor={{
            screenPosition: contextAnchorStopChip.screenPosition,
          }}
          stops={(stops ?? []).filter((s: Stop) => selectedStops.includes(s.id))}
        />
      )}
    </StopChipContainer>
  );
};
